import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useCanGoBack, useRouter } from '@tanstack/react-router'
import { ArrowLeftIcon, LayoutDashboardIcon, LogOutIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { useSignOut } from '#admin/features/auth/sign-out.ts'

/**
 * A page that cannot show what was asked for — refused, absent, or broken — **and says what to do
 * next.**
 *
 * The screen it replaces said "Not permitted. You do not have permission to do that." and nothing
 * else: no way back, no way to the dashboard, no hint whose permission was missing or who to ask.
 * With the router's defaults now pointing here it renders *inside* the console frame, so the
 * sidebar is still there too; these actions are for the person who does not think to use it.
 *
 * Every state offers the same three exits, because they are the three things somebody stopped here
 * actually does: go back to where they were, start again from the dashboard, or — when the problem
 * is that they are signed in as the wrong person — sign out and switch.
 *
 * `role="status"` rather than `alert`: the state *is* the page, present on arrival, and an assertive
 * announcement would interrupt the screen reader reading it.
 */
export function PageState({
  icon,
  title,
  children,
  actions,
  offerSignOut = false,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
  /** Extra actions, before the standard exits — "Try again", "Reload". */
  actions?: ReactNode
  /** For refusals, where signing in as somebody else is a real remedy. */
  offerSignOut?: boolean
}) {
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const { data: session } = useQuery(sessionQuery())
  const signOut = useSignOut()
  const signedInAs = session?.user?.email

  return (
    <section
      role="status"
      className="mx-auto flex max-w-lg flex-col items-center gap-4 px-4 py-16 text-center"
    >
      <div
        aria-hidden="true"
        className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full [&>svg]:size-6"
      >
        {icon}
      </div>
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold text-balance">{title}</h1>
        <div className="text-muted-foreground text-sm text-pretty">{children}</div>
        {offerSignOut && signedInAs !== undefined ? (
          <p className="text-muted-foreground text-xs">
            Signed in as <span className="text-foreground font-medium">{signedInAs}</span>
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {actions}
        {canGoBack ? (
          <Button variant="outline" onClick={() => router.history.back()}>
            <ArrowLeftIcon />
            Go back
          </Button>
        ) : null}
        <Button asChild variant={actions === undefined ? 'default' : 'outline'}>
          <Link to="/dashboard">
            <LayoutDashboardIcon />
            Go to dashboard
          </Link>
        </Button>
        {offerSignOut &&
        (session?.user ?? null) !== null &&
        (session?.impersonator ?? null) === null ? (
          <Button variant="ghost" disabled={signOut.isPending} onClick={() => signOut.mutate()}>
            <LogOutIcon />
            Sign in as someone else
          </Button>
        ) : null}
      </div>
    </section>
  )
}
